// Phase 27 (B-13) — Buyer opens a dispute via a dedicated form page.
//
// Replaces the legacy window.prompt() in MyPurchasesPage which was UX
// poor (no validation feedback, looked like a phishing artefact on
// some browsers, no place to explain context). The backend endpoint
// is the same — POST /marketplace/sales/:id/dispute with
// `{ reason: string (10-2000 chars) }` — see shared/src/schemas/marketplace.ts.
//
// Categorisation is purely client-side guidance — the backend only
// stores the free-text reason. Picking a category prefixes the
// submitted reason for AEAT/admin readability.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { getSale, openDispute } from '../api/marketplace';
import type { MarketplaceSaleDetail } from '@leyrochain/shared';

const CATEGORIES: Array<{ value: string; label: string; hint: string }> = [
  {
    value: 'health',
    label: 'Animal con problemas de saúde',
    hint: 'Síntomas, vacinas incorrectas, sospeita de enfermidade…',
  },
  {
    value: 'identity',
    label: 'Identidade do animal non coincide',
    hint: 'O crotal, idade, raza ou sexo entregado non coincide co contrato.',
  },
  {
    value: 'docs',
    label: 'Documentación incorrecta ou ausente',
    hint: 'DIB ou guía SITRAN inválidos, datos discrepantes coa realidade.',
  },
  {
    value: 'not_delivered',
    label: 'Non recibín o animal',
    hint: 'O animal non chegou ou non se puido recoller.',
  },
  {
    value: 'other',
    label: 'Outro',
    hint: 'Calquera outro motivo non previsto arriba.',
  },
];

const MIN_CHARS = 10;
const MAX_CHARS = 2000;

export default function DisputeOpenPage(): JSX.Element {
  const { saleId } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const [sale, setSale] = useState<MarketplaceSaleDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('health');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!saleId) return;
    let cancelled = false;
    void (async () => {
      try {
        const s = await getSale(saleId);
        if (!cancelled) setSale(s);
      } catch {
        if (!cancelled) setLoadError('Non se puido cargar a venda.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [saleId]);

  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen >= MIN_CHARS && trimmedLen <= MAX_CHARS && !submitting;
  const remaining = MAX_CHARS - trimmedLen;
  const selectedCategory = useMemo(
    () => CATEGORIES.find((c) => c.value === category) ?? CATEGORIES[0],
    [category],
  );

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit || !saleId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const prefix = selectedCategory ? `[${selectedCategory.label}] ` : '';
      await openDispute(saleId, { reason: prefix + reason.trim() });
      navigate('/marketplace/me/purchases', { replace: true, state: { disputeOpened: true } });
    } catch (err) {
      const msg = err as { response?: { data?: { reason?: string } }; message?: string };
      setSubmitError(
        msg.response?.data?.reason ??
          msg.message ??
          'Non se puido abrir a disputa. Téntao de novo.',
      );
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-extrabold text-dark">Disputa</h1>
        <p className="text-sm text-danger mt-3">{loadError}</p>
        <Link
          to="/marketplace/me/purchases"
          className="inline-block mt-6 px-4 py-2 rounded-lg border border-border text-dark"
        >
          Volver ás miñas compras
        </Link>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="h-8 w-48 bg-border/40 rounded animate-pulse mb-4" />
        <div className="h-32 w-full bg-border/40 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <div className="text-xs text-muted">
        <Link to="/marketplace/me/purchases" className="hover:underline">
          ← As miñas compras
        </Link>
      </div>
      <h1 className="text-2xl font-extrabold text-rauxa mt-3">Abrir unha disputa</h1>
      <p className="text-sm text-muted mt-1">
        Explica o problema co animal recibido. O administrador revisará o caso e contactarate cunha
        resolución (reembolso, confirmación da venda ou intermediación). Recibirás unha notificación
        cando estea resolto.
      </p>

      {/* Sale summary */}
      <div className="mt-6 p-4 rounded-xl bg-white border border-border flex gap-4 items-center">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-border/40 shrink-0 flex items-center justify-center text-3xl">
          {sale.animal.photo_url ? (
            <img
              src={sale.animal.photo_url}
              alt={sale.animal.name ?? sale.animal.crotal_id}
              className="w-full h-full object-cover"
            />
          ) : (
            <span>🐄</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-dark truncate">
            {sale.animal.name ?? sale.animal.crotal_id}
          </p>
          <p className="text-xs text-muted font-mono">
            {[sale.animal.crotal_id, sale.animal.breed].filter(Boolean).join(' · ')}
          </p>
          <p className="text-xs text-muted mt-1">
            Vendedor: {sale.seller.farm_name ?? sale.seller.name}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-6 space-y-5">
        <div>
          <label className="block text-sm font-semibold text-dark mb-2" htmlFor="category">
            Tipo de problema
          </label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-white text-dark text-sm focus:outline-none focus:ring-2 focus:ring-rauxa"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted mt-1">{selectedCategory.hint}</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-dark mb-2" htmlFor="reason">
            Detalles do problema
          </label>
          <textarea
            id="reason"
            rows={8}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Describe o que pasou, cando, e calquera detalle relevante. Sé o máis específico posible — o administrador usará esta información para decidir."
            className="w-full px-3 py-2 rounded-lg border border-border bg-white text-dark text-sm focus:outline-none focus:ring-2 focus:ring-rauxa resize-y"
          />
          <p
            className={`text-xs mt-1 ${
              trimmedLen < MIN_CHARS
                ? 'text-muted'
                : remaining < 100
                  ? 'text-amber-600'
                  : 'text-muted'
            }`}
          >
            {trimmedLen < MIN_CHARS
              ? `Mínimo ${MIN_CHARS} caracteres (faltan ${MIN_CHARS - trimmedLen}).`
              : `${trimmedLen} / ${MAX_CHARS} caracteres.`}
          </p>
        </div>

        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
          <p className="font-semibold mb-1">Importante</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>A disputa congelará o pagamento ao vendedor ata a resolución.</li>
            <li>Só podes abrir unha disputa dentro das 72 horas seguintes á recepción.</li>
            <li>Adxunta evidencias (fotos, informes vet) por email a admin@leyro.es se as tes.</li>
          </ul>
        </div>

        {submitError ? (
          <div
            role="alert"
            className="p-3 rounded-lg bg-red-50 border border-danger text-danger text-sm"
          >
            {submitError}
          </div>
        ) : null}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex-1 py-2.5 rounded-lg bg-danger text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Abrindo disputa…' : 'Abrir disputa'}
          </button>
          <Link
            to="/marketplace/me/purchases"
            className="px-4 py-2.5 rounded-lg border border-border text-dark"
          >
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  );
}
